import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart, useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  ChevronLeft,
  MessageCircle,
  FileText,
  ShoppingBag,
  CheckCircle,
} from "lucide-react";

export default function Carrito() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, updateCartItem, clearCart } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderType, setOrderType] = useState("order"); // "order" or "quote"
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    customer_city: "",
    notes: "",
  });

  // Pre-fill form with user data when available
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || "",
        customer_email: user.email || "",
        customer_phone: user.phone || "",
        customer_address: user.address || "",
        customer_city: user.city || "",
      }));
    }
  }, [user]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-UY", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const handleQuantityChange = (productId, currentQty, delta) => {
    const newQty = currentQty + delta;
    if (newQty <= 0) {
      updateCartItem(productId, 0);
    } else {
      updateCartItem(productId, newQty);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone) {
      toast.error("Por favor completá todos los campos obligatorios");
      return;
    }

    // Check if user is approved for orders
    if (user && user.status !== "approved" && orderType === "order") {
      toast.error("Tu cuenta debe estar aprobada para realizar pedidos. Podés solicitar una cotización.");
      return;
    }

    setIsSubmitting(true);
    try {
      const orderData = {
        order_type: orderType,
        ...formData,
        items: cart.items.map((item) => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          image: item.image,
        })),
      };

      await api.post("/orders", orderData);
      
      if (orderType === "order") {
        toast.success("¡Pedido enviado! Te contactaremos para confirmar.");
      } else {
        toast.success("¡Cotización solicitada! Te enviaremos el presupuesto pronto.");
      }
      
      clearCart();
      navigate("/mi-cuenta");
    } catch (error) {
      toast.error("Error al enviar. Intentá de nuevo.");
      console.error("Order error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppOrder = () => {
    let message = "Hola! Quiero hacer un pedido:\n\n";
    cart.items.forEach((item) => {
      message += `• ${item.name} x${item.quantity} - ${formatPrice(item.price * item.quantity)}\n`;
    });
    message += `\n*Total: ${formatPrice(cart.total)}*`;
    
    const whatsappUrl = `https://wa.me/59899179773?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  if (cart.items?.length === 0) {
    return (
      <div className="min-h-screen bg-background" data-testid="carrito-empty">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <ShoppingCart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="font-heading text-2xl md:text-3xl mb-2">TU CARRITO ESTÁ VACÍO</h1>
          <p className="text-muted-foreground mb-6">Agregá productos para comenzar tu pedido</p>
          <Link to="/tienda">
            <Button className="bg-primary hover:bg-primary/90">
              Ver Productos
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="carrito-page">
      {/* Header */}
      <div className="bg-primary text-white py-8 relative">
        <div className="relative industrial-texture"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl">MI CARRITO</h1>
          <p className="text-white/80 mt-2">
            {cart.items?.length} producto{cart.items?.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/tienda" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ChevronLeft className="w-4 h-4" />
          Seguir comprando
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items?.map((item) => (
              <div
                key={item.product_id}
                className="bg-white rounded-sm border p-4 flex gap-4"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Image */}
                <div className="w-24 h-24 flex-shrink-0 bg-muted rounded overflow-hidden">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                      Sin imagen
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <Link to={`/producto/${item.product_id}`}>
                    <h3 className="font-heading text-sm md:text-base hover:text-primary line-clamp-2">
                      {item.name}
                    </h3>
                  </Link>
                  <p className="text-accent font-heading text-lg mt-1">
                    {formatPrice(item.price)}
                  </p>

                  {/* Quantity controls */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border rounded">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-none"
                        onClick={() => handleQuantityChange(item.product_id, item.quantity, -1)}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-10 text-center text-sm">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-none"
                        onClick={() => handleQuantityChange(item.product_id, item.quantity, 1)}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => updateCartItem(item.product_id, 0)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Subtotal */}
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-muted-foreground">Subtotal</p>
                  <p className="font-heading text-lg">
                    {formatPrice(item.price * item.quantity)}
                  </p>
                </div>
              </div>
            ))}

            {/* Clear cart */}
            <Button
              variant="ghost"
              onClick={clearCart}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Vaciar carrito
            </Button>
          </div>

          {/* Order summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-sm border p-6 sticky top-28">
              <h2 className="font-heading text-xl mb-4">RESUMEN</h2>

              <div className="space-y-2 mb-4">
                {cart.items?.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground truncate pr-2">
                      {item.name} x{item.quantity}
                    </span>
                    <span>{formatPrice(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <Separator className="my-4" />

              <div className="flex justify-between items-center mb-6">
                <span className="font-heading text-lg">TOTAL</span>
                <span className="font-heading text-2xl text-accent">
                  {formatPrice(cart.total)}
                </span>
              </div>

              {!showCheckout ? (
                <div className="space-y-3">
                  {/* Order type selection */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setOrderType("order")}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        orderType === "order"
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-border hover:border-accent/50"
                      }`}
                    >
                      <ShoppingBag className="w-6 h-6 mx-auto mb-1" />
                      <p className="font-medium text-sm">Pedido</p>
                      <p className="text-xs text-muted-foreground">Confirmo la compra</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setOrderType("quote")}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        orderType === "quote"
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <FileText className="w-6 h-6 mx-auto mb-1" />
                      <p className="font-medium text-sm">Cotización</p>
                      <p className="text-xs text-muted-foreground">Solo presupuesto</p>
                    </button>
                  </div>

                  <Button
                    onClick={() => setShowCheckout(true)}
                    className={`w-full font-bold uppercase tracking-wide h-12 ${
                      orderType === "order" 
                        ? "bg-accent hover:bg-accent/90" 
                        : "bg-primary hover:bg-primary/90"
                    } text-white`}
                    data-testid="proceed-checkout-btn"
                  >
                    {orderType === "order" ? "Realizar Pedido" : "Solicitar Cotización"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmitOrder} className="space-y-4">
                  {/* Order type indicator */}
                  <div className={`flex items-center gap-2 p-3 rounded-lg ${
                    orderType === "order" 
                      ? "bg-accent/10 text-accent border border-accent/20" 
                      : "bg-primary/10 text-primary border border-primary/20"
                  }`}>
                    {orderType === "order" ? (
                      <>
                        <ShoppingBag className="w-5 h-5" />
                        <div>
                          <p className="font-medium text-sm">Pedido de compra</p>
                          <p className="text-xs opacity-80">Te contactaremos para confirmar</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5" />
                        <div>
                          <p className="font-medium text-sm">Solicitud de cotización</p>
                          <p className="text-xs opacity-80">Te enviaremos el presupuesto</p>
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="name">Nombre *</Label>
                    <Input
                      id="name"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      required
                      data-testid="checkout-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      required
                      data-testid="checkout-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Teléfono / WhatsApp *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      required
                      placeholder="Ej: 099123456"
                      data-testid="checkout-phone"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="city">Ciudad</Label>
                      <Input
                        id="city"
                        value={formData.customer_city}
                        onChange={(e) => setFormData({ ...formData, customer_city: e.target.value })}
                        placeholder="Maldonado"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address">Dirección</Label>
                      <Input
                        id="address"
                        value={formData.customer_address}
                        onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                        placeholder="Calle 123"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">Notas adicionales</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Instrucciones especiales, horarios, etc."
                      rows={3}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full font-bold uppercase tracking-wide h-12 ${
                      orderType === "order" 
                        ? "bg-accent hover:bg-accent/90" 
                        : "bg-primary hover:bg-primary/90"
                    } text-white`}
                    data-testid="submit-order-btn"
                  >
                    {isSubmitting 
                      ? "Enviando..." 
                      : orderType === "order" 
                        ? "Confirmar Pedido" 
                        : "Solicitar Cotización"
                    }
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowCheckout(false)}
                    className="w-full"
                  >
                    Volver
                  </Button>
                </form>
              )}

              <p className="text-xs text-muted-foreground text-center mt-4">
                Precios en USD, IVA incluido
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
